import {
	PreformOperationResponse,
	PreformOperationUpdateRequest,
	PreformTechMapResponse,
	PreformOperationsUpdateOrCreateRequest,
	useUpdatePreformTechMapMutation,
} from '@/shared/state/api/swagger';
import { COMPLECT_OPERATION_STEP_ID } from '@/shared';

export const usePreformOperations = () => {
	const [updatePreformTechMap] = useUpdatePreformTechMapMutation();

	const handleCurrentOperation = (data: PreformOperationUpdateRequest, techMap: PreformTechMapResponse) => {
		return new Promise<PreformTechMapResponse>((resolve, reject) => {
			try {
				const { is_canceled, id } = techMap.current_preform_operation as PreformOperationResponse;
				const { preform_operations: operations } = techMap;
				if (!id || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: PreformOperationUpdateRequest = {
					...data,
					is_canceled,
					id,
				};

				// вставляем объект операции на место измененного
				const operationsToUpdate = operations.map((op) =>
					op.id === id ? payload : (op as PreformOperationUpdateRequest),
				);

				// Для сборочных техкарт реализуем логику переноса значений через getAllOperationsWithCopyFields
				const { operationsWithCopyFields } = getAllOperationsWithCopyFields(payload, operationsToUpdate);

				const operationsPayload: PreformOperationsUpdateOrCreateRequest = {
					preform_operations_to_update: operationsWithCopyFields,
				};

				updatePreformTechMap({
					preformTechMapId: techMap.id,
					bodyUpdatePreformTechMap: {
						preform_operations_payload: operationsPayload,
					},
				})
					.unwrap()
					.then((res) => {
						resolve(res);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update preform operation error:', error);
				reject(error);
			}
		});
	};

	const handleNotCurrentOperation = (data: PreformOperationUpdateRequest, techMap: PreformTechMapResponse) => {
		return new Promise<PreformOperationResponse[]>((resolve, reject) => {
			try {
				// Используем операции непосредственно из техкарты
				const operations = techMap.preform_operations || [];

				// достаем значения предыдущего состояния измененного объекта операции
				const foundOperation = operations.find((op) => op.id === data.id);
				if (!foundOperation) return;

				const { id, is_canceled } = foundOperation;

				if (!id || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: PreformOperationUpdateRequest = {
					...data,
					id,
					is_canceled,
				};

				// вставляем объект операции на место измененного
				const operationsToUpdate = operations.map((op) =>
					op.id === id ? payload : (op as PreformOperationUpdateRequest),
				);

				const { operationsWithCopyFields } = getAllOperationsWithCopyFields(payload, operationsToUpdate);

				const operationsPayload: PreformOperationsUpdateOrCreateRequest = {
					preform_operations_to_update: operationsWithCopyFields,
				};

				// Отслеживаем пропущенные операции для возврата
				const allOperations = [...operationsWithCopyFields];

				const currentOperationIdx = allOperations.findIndex(
					(op) => op.id === techMap.current_preform_operation?.id,
				);
				const selectedOperationIdx = allOperations.findIndex((op) => op.id === data.id);
				const skippedOperations = allOperations.slice(currentOperationIdx, selectedOperationIdx);

				updatePreformTechMap({
					preformTechMapId: techMap.id,
					bodyUpdatePreformTechMap: {
						preform_operations_payload: operationsPayload,
					},
				})
					.unwrap()
					.then(() => {
						resolve(skippedOperations as PreformOperationResponse[]);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update preform operation error: ', error);
				reject(error);
			}
		});
	};

	return { handleCurrentOperation, handleNotCurrentOperation };
};

// функция для переноса годных шт в поля кол-во шт - адаптированная для preform operations
const getAllOperationsWithCopyFields = (
	payload: PreformOperationUpdateRequest,
	operationsPayload: PreformOperationUpdateRequest[],
) => {
	// Если это операция комплектования, не выполняем переносы
	if (payload.step_id === COMPLECT_OPERATION_STEP_ID) {
		return { operationsWithCopyFields: operationsPayload };
	}

	// Сортируем операции по step_id для корректного порядка
	const operations = [...operationsPayload].sort((a, b) => a.id - b.id);

	// Находим индекс заполняемой операции
	const filledCustomOperationIndex = operations.findIndex((op) => op.id === payload.id);

	if (filledCustomOperationIndex !== operations.length - 1) {
		operations[filledCustomOperationIndex + 1] = {
			...operations[filledCustomOperationIndex + 1],
			count_in_number: payload.count_out_number,
		};
	}

	return { operationsWithCopyFields: operations };
};
